import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { LEVELS, levelOf } from "@/lib/access";
import { toast } from "sonner";
import { ChevronRight, Download, FolderPlus, HardDrive, Loader2, PencilLine, Trash2, Upload } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { FolderIcon } from "@/components/files/FolderIcon";
import { date, iconFor, size, tool } from "@/lib/files";

export default function FilesPage({ embedded = false }) {
  const { isAdmin, user } = useAdmin();
  const [params, setParams] = useSearchParams();
  const folderId = params.get("folder") || "";
  const [data, setData] = useState({ folder: null, path: [], folders: [], files: [] });
  const [busy, setBusy] = useState(0);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => {
    api.get("/drive", { params: folderId ? { folder_id: folderId } : {} })
      .then((r) => setData({ folder: r.data?.folder || null, path: r.data?.path || [], folders: r.data?.folders || [], files: r.data?.files || [] }))
      .catch((e) => { toast.error(errText(e)); if (folderId) setParams({}); });
  }, [folderId, setParams]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!isAdmin) return <NotFound />;

  const open = (id) => setParams(id ? { folder: id } : {});
  const newFolder = async () => {
    const name = window.prompt("Folder name:", "New folder");
    if (!name?.trim()) return;
    try { await api.post("/drive/folders", { name: name.trim(), parent_id: folderId || null }); load(); } catch (e) { toast.error(errText(e)); }
  };
  const rename = async (kind, item) => {
    const name = window.prompt("New name:", item.name);
    if (!name?.trim() || name === item.name) return;
    try { await api.put(`/drive/${kind}/${item.id}`, { name: name.trim() }); load(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (kind, item) => {
    const msg = kind === "folders" ? `Delete the folder "${item.name}" and everything inside it?` : `Delete "${item.name}"?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/drive/${kind}/${item.id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(errText(e)); }
  };
  const upload = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    setBusy(files.length);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); if (folderId) fd.append("folder_id", folderId);
      try { await api.post("/drive/files", fd); } catch (e) { toast.error(`${f.name}: ${errText(e, "upload failed")}`); }
      setBusy((b) => b - 1);
    }
    load();
  };
  const download = async (f) => {
    try {
      const r = await api.get(`/drive/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Download failed"); }
  };

  const empty = !data.folders.length && !data.files.length;
  return (
    <div data-testid="files-page" className={embedded ? "" : "mx-auto max-w-7xl px-5 pb-28 pt-10 sm:px-8 lg:px-12"}>
      <div className={`flex flex-wrap items-end gap-4 ${embedded ? "justify-end" : "justify-between"}`}>
        {!embedded && <div>
          <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">Private · only you can see this</p>
          <h1 className="mt-3 font-display text-5xl font-extrabold tracking-tight sm:text-6xl">Files</h1>
        </div>}
        <div className="flex gap-2">
          <button onClick={newFolder} data-testid="files-new-folder" className="flex items-center gap-2 border border-black/80 px-4 py-2.5 text-sm font-bold hover:bg-black hover:text-white"><FolderPlus className="h-4 w-4" />New folder</button>
          <button onClick={() => input.current?.click()} disabled={busy > 0} data-testid="files-upload" className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? `Uploading (${busy})…` : "Upload"}
          </button>
          <input ref={input} type="file" multiple className="hidden" data-testid="files-input" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      <div className="mt-8 border border-black/15 bg-white shadow-sm">
        <nav className="flex flex-wrap items-center gap-1 border-b border-black/10 bg-[#F3F2EF] px-4 py-2.5 text-sm" data-testid="files-breadcrumb">
          <button onClick={() => open("")} className="flex items-center gap-1.5 rounded px-2 py-1 font-semibold hover:bg-black/10"><HardDrive className="h-4 w-4" />Files</button>
          {data.path.map((p) => (
            <span key={p.id} className="flex items-center gap-1"><ChevronRight className="h-4 w-4 text-[#57534E]" />
              <button onClick={() => open(p.id)} className="rounded px-2 py-1 font-semibold hover:bg-black/10">{p.name}</button></span>
          ))}
          {data.folder && (
            <label className="ml-auto flex items-center gap-2 text-xs text-[#57534E]">Level
              <select value={data.folder.level || "confidential"} aria-label="Permission level for this folder" data-testid="current-folder-level"
                onChange={async (e) => { const level = e.target.value; try { await api.put(`/drive/folders/${data.folder.id}/access`, { level }); toast.success(`${data.folder.name} is now ${levelOf(level).label}`); load(); } catch (err) { toast.error(errText(err)); } }}
                className="cursor-pointer border border-black/20 bg-white px-2 py-1 text-xs font-semibold text-[#0A0A0A] outline-none">
                {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </label>
          )}
        </nav>
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          className={`min-h-[360px] p-4 transition-colors sm:p-6 ${drag ? "bg-[#E62314]/5 outline-dashed outline-2 -outline-offset-8 outline-[#E62314]" : ""}`}>
          {data.folders.length > 0 && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8" data-testid="files-folders">
              {data.folders.map((f) => (
                <div key={f.id} className="group relative flex flex-col items-center rounded p-2 text-center hover:bg-[#E8F0FE]" data-testid={`folder-${f.id}`}>
                  <button onClick={() => open(f.id)} className="flex w-full flex-col items-center" data-testid={`folder-open-${f.id}`}>
                    <FolderIcon className="h-16 w-20 transition-transform group-hover:-translate-y-0.5" />
                    <span className="mt-1 w-full break-words text-sm font-semibold [hyphens:none]">{f.name}</span>
                    <span className="text-[11px] text-[#57534E]">{f.count} {f.count === 1 ? "item" : "items"}</span>
                  </button>
                  <div className="absolute right-1 top-1 flex gap-0.5 rounded bg-white/90 shadow md:hidden md:group-hover:flex">
                    <button className={tool} onClick={() => rename("folders", f)} aria-label="Rename folder" data-testid={`folder-rename-${f.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                    <button className={tool} onClick={() => remove("folders", f)} aria-label="Delete folder" data-testid={`folder-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {data.files.length > 0 && (
            <table className="mt-6 w-full text-left text-sm" data-testid="files-table">
              <thead className="border-b border-black/10 text-xs uppercase tracking-wider text-[#57534E]">
                <tr><th className="py-2 font-semibold">Name</th><th className="hidden py-2 font-semibold sm:table-cell">Size</th><th className="hidden py-2 font-semibold md:table-cell">Added</th><th /></tr>
              </thead>
              <tbody>
                {data.files.map((f) => { const [Icon, color] = iconFor(f); return (
                  <tr key={f.id} className="group border-b border-black/5 hover:bg-[#E8F0FE]" data-testid={`file-${f.id}`}>
                    <td className="py-2.5"><span className="flex items-start gap-3 text-left font-semibold"><Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color}`} /><span className="min-w-0"><button onClick={() => download(f)} className="block break-words text-left [hyphens:none] hover:underline">{f.name}</button>{!data.folder && <select value={f.level || "confidential"} aria-label={`Level for ${f.name}`} data-testid={`file-level-${f.id}`}
                      onChange={async (e) => { const level = e.target.value; try { await api.put(`/drive/files/${f.id}/level`, { level }); toast.success(`${f.name} is now ${levelOf(level).label}`); load(); } catch (err) { toast.error(errText(err)); } }}
                      className="mt-0.5 block cursor-pointer appearance-none bg-transparent p-0 text-[11px] font-normal text-[#57534E] outline-none hover:text-[#0A0A0A]">
                      {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </select>}</span></span></td>
                    <td className="hidden py-2.5 text-[#57534E] sm:table-cell">{size(f.size)}</td>
                    <td className="hidden py-2.5 text-[#57534E] md:table-cell">{date(f.created_at)}</td>
                    <td className="py-2.5"><div className="flex justify-end gap-0.5">
                      <button className={tool} onClick={() => download(f)} aria-label="Download" data-testid={`file-download-${f.id}`}><Download className="h-4 w-4" /></button>
                      <button className={tool} onClick={() => rename("files", f)} aria-label="Rename" data-testid={`file-rename-${f.id}`}><PencilLine className="h-4 w-4" /></button>
                      <button className={tool} onClick={() => remove("files", f)} aria-label="Delete" data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>); })}
              </tbody>
            </table>
          )}
          {empty && (
            <div className="flex min-h-[300px] flex-col items-center justify-center text-center text-[#57534E]" data-testid="files-empty">
              <FolderIcon className="h-20 w-24 opacity-60" />
              <p className="mt-4 text-base font-semibold text-[#0A0A0A]">This folder is empty</p>
              <p className="mt-1 text-sm">Create a folder, click Upload, or drag files here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
